import * as s from "./SubHeaderStyled";
import BigButton from "components/atoms/buttons/BigButton";
import SearchInput, {
  ClickSearch,
} from "components/atoms/input/search/SearchInput";
import { useLocation } from "react-router-dom";
import type { RoutesProp } from "api/interfaces/menuInterface.interface";
import { useAppSelector } from "redux/hooks";
import { noop } from "lodash";
import { BsChevronRight } from "react-icons/bs";
import * as mediaQuery from "components/MediaQuery";
import RoutesMark from "../headernav/sub/RoutesMark";
import RBACWrapper from "../function/RBACWrapper";
import { Fragment, useMemo } from "react";
import {
  ProfileAccountInfo,
} from "api/interfaces/accountInterface.interface";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import { RequiredPermissionType } from "utils/MenuUtil";
import { useMenu } from "hook/useMenu";

export type TitleComponentType =
  | string
  | React.ReactNode
  | Array<TitleComponentArrayType>;
export interface HeaderRoutesProps {
  name: string;
  routeName?: string;
  accountId?: string;
}
export interface TitleComponentArrayType {
  label: string | React.ReactNode;
  onClick?: () => void | undefined;
  isNoshow?: boolean;
}

export interface SubHeaderProps {
  onClickBtn?: () => void; // Add 버튼 이벤트
  buttonLabel?: string | null; // Add 버튼 라벨
  status?: boolean; // 상태
  centerChildren?: React.ReactNode | null; //상태값이나 메뉴들과 같은 컴포넌트 children
  keyword?: string;
  onClickSearch?: ClickSearch; // 검색 돋보기 버튼 클릭 이벤트
  titleComponent?: TitleComponentType;
  noSearch?: boolean; // 검색창 없을 때
  buttons?: React.ReactNode;
  requiredPermissions?: string; // button Permission
  licenseInfo?: React.ReactNode | null;
  forwardSearch?: React.ReactNode | null;
  titleDescription?: React.ReactNode | null;
}

function SubHeader({
  buttonLabel,
  onClickBtn,
  centerChildren,
  onClickSearch = () => {},
  keyword,
  titleComponent,
  noSearch,
  buttons,
  requiredPermissions,
  licenseInfo,
  forwardSearch,
  titleDescription,
}: SubHeaderProps): JSX.Element {
  const location = useLocation();
  const { currentMenu } = useMenu({});
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  let submenu: Array<RoutesProp> | [] = useMemo(() => {
    return (currentMenu?.children ?? []).filter((item) => !!item.name);
  }, [currentMenu?.children]);

  const isEULicense =
    location.pathname.includes("/recorders") &&
    selectedAccount.accountLevel === LEVEL_TYPE.EU;

  const showRouter = useMemo(
    () =>
      location.pathname.includes("/recorders") ||
      location.pathname.includes("/subscription/organization") ||
      location.pathname.startsWith("/account"),
    [location.pathname]
  );
  const titleReturn = (
    titleComponent: string | React.ReactNode | Array<TitleComponentArrayType>
  ) => {
    if (titleComponent) {
      if (Array.isArray(titleComponent)) {
        return (
          <s.Title className="title-breadcrumb">
            <s.TitleWrapper className="title-breadcrumb-wrapper">
              {titleComponent.map(
                (item, index) =>
                  !item.isNoshow && (
                    <Fragment key={index}>
                      {index > 0 && (
                        <li className="chevron-right">
                          <BsChevronRight size={12} />
                        </li>
                      )}

                      <s.TitleCrumb
                        key={index}
                        index={index}
                        onClick={item.onClick ? item.onClick : noop}
                        data-testid="blocks-subheader-title-crumb-click"
                      >
                        {item.label}
                      </s.TitleCrumb>
                    </Fragment>
                  )
              )}
            </s.TitleWrapper>
          </s.Title>
        );
      }
      if (typeof titleComponent === "string")
        return (
          <s.Title className="title-component">
            <s.FirstTitle className="title-component-title">
              {titleComponent}
            </s.FirstTitle>
          </s.Title>
        );

      return titleComponent;
    } else
      return (
        <s.Title className="title-breadcrumb">
          <s.FirstTitle className="title-breadcrumb-title">
            {submenu.length > 0
              ? submenu.map((item: RoutesProp, index: number) => (
                  <Fragment key={index}>
                    {location.pathname.includes(item.routeName as string) &&
                      item.title}
                    {/* {location.pathname === item.routeName && item.title} */}
                  </Fragment>
                ))
              : currentMenu?.name}
          </s.FirstTitle>
        </s.Title>
      );
  };

  return (
    <s.SubHeader className="subheader">
      <s.TopWrapper className="routes-mark-wrapper">
        <mediaQuery.Default>
          {showRouter && <RoutesMark />}
          {isEULicense && licenseInfo}
        </mediaQuery.Default>
      </s.TopWrapper>

      <s.HeaderContainer className="header-container">
        <mediaQuery.Mobile>
          <s.MobileRow className="mobile-row">
            {titleReturn(titleComponent)}
            {titleDescription && (
              <s.TitleDescription>{titleDescription}</s.TitleDescription>
            )}

            {centerChildren && <s.CenterAside>{centerChildren}</s.CenterAside>}
          </s.MobileRow>
        </mediaQuery.Mobile>
        <mediaQuery.Default>
          <s.TitleLeft className="title-left">
            <s.TitleTextWrapper>
              {titleReturn(titleComponent)}
              {titleDescription && (
                <s.TitleDescription>{titleDescription}</s.TitleDescription>
              )}
            </s.TitleTextWrapper>
            {centerChildren && <s.CenterAside>{centerChildren}</s.CenterAside>}
          </s.TitleLeft>
        </mediaQuery.Default>

        <s.RightAside className="right-aside">
          <mediaQuery.Mobile>
            {buttons && buttons}
            {forwardSearch && forwardSearch}
            {!noSearch && (
              <s.SearchInputWrapper>
                <SearchInput
                  keyword={keyword}
                  placeholder="Search"
                  onClickSearch={onClickSearch}
                />
              </s.SearchInputWrapper>
            )}
          </mediaQuery.Mobile>
          <mediaQuery.Default>
            {forwardSearch && forwardSearch}
            {!noSearch && (
              <s.SearchInputWrapper>
                <SearchInput
                  keyword={keyword}
                  placeholder="Search"
                  onClickSearch={onClickSearch}
                />
              </s.SearchInputWrapper>
            )}

            {buttonLabel && onClickBtn && (
              <RBACWrapper
                requiredPermissions={
                  requiredPermissions as RequiredPermissionType
                }
              >
                <BigButton
                  id={"dw_button_".concat(
                    buttonLabel.replace(/(\s*)/g, "").toLowerCase()
                  )}
                  label={buttonLabel}
                  add
                  onClickBtn={onClickBtn}
                  data-testid="blocks-subheader-button-click"
                />
              </RBACWrapper>
            )}

            {buttons && buttons}
          </mediaQuery.Default>
        </s.RightAside>
      </s.HeaderContainer>
    </s.SubHeader>
  );
}

export default SubHeader;
